import { inject, Injectable, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { AirPath, AirPathDirection } from "@domain/project/floorplan/air-path";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { getKindForItem } from "@domain/project/floorplan/floorplan-item.utils";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanSelectionMode, FloorplanState } from "@domain/project/floorplan/floorplan-state";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone, DustExZoneType } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone, ExZoneType } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone, WildcardZoneType } from "@domain/project/floorplan/zones/wildcard-zone";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { FloorplanWorkspace } from "@project/floorplanner/floorplan-workspace";
import { FloorplanWorkspaceItemService } from "@project/floorplanner/floorplan-workspace-item.service";
import { FloorplanWorkspaceWarningSignService } from "@project/floorplanner/floorplan-workspace-warning-sign.service";
import { ItemSelectIntention } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { ProjectStateService } from "@project/project-state.service";
import {
  DraggedItem,
  DraggedItemType,
  DropEvent,
  DropPosition,
  FloorplanDragDropService,
} from "@utils/floorplan-drag-drop.service";
import { Point } from "@utils/point";
import { BehaviorSubject, filter, Observable, Subject, switchMap, takeUntil, tap } from "rxjs";
import { take } from "rxjs/operators";

type ReferenceSizeZone =
  DangerArea | ExZone | DustExZone | WildcardZone | IsolationRoom | ItRoom | OperatingTheatre | ProcedureRoom | StandardRoom;

type CopiedFloorplanSnapshot = {
  snapshot: (x: number, y: number) => FloorplanItem;
  x: number;
  y: number;
};

type CopiedFloorplanState =
  { type: "single"; snapshot: (x: number, y: number) => FloorplanItem } | { type: "multi"; snapshots: CopiedFloorplanSnapshot[] };

@Injectable()
export class FloorplanService implements OnDestroy {
  private floorplanWorkspace = inject(FloorplanWorkspace);
  private projectStateService = inject(ProjectStateService);
  private floorplanWorkspaceItemService = inject(FloorplanWorkspaceItemService);
  private floorplanWorkspaceWarningSignService = inject(FloorplanWorkspaceWarningSignService);
  private dragDropService = inject(FloorplanDragDropService);
  private projectFloorplanService = inject(ProjectFloorplanService);

  private _isReferenceMeasureSet$: BehaviorSubject<boolean>;
  public isReferenceMeasureSet$: Observable<boolean>;

  private static readonly DEFAULT_ICON_SIZE = 4;

  readonly selectedFloorplan: Floorplan;
  private selectedFloorplanItem?: FloorplanItem;
  private selectedFloorplanItems: FloorplanItem[] = [];
  private copiedFloorplanState?: CopiedFloorplanState;
  private suppressAddedItemSelection = false;

  private currentIconSize = FloorplanService.DEFAULT_ICON_SIZE;
  private readonly destroyed$ = new Subject<void>();

  constructor() {
    const route = inject(ActivatedRoute);
    this.selectedFloorplan = route.snapshot.data["floorplan"];
    this._isReferenceMeasureSet$ = new BehaviorSubject<boolean>(!!this.selectedFloorplan?.referenceMeasure);
    this.isReferenceMeasureSet$ = this._isReferenceMeasureSet$.asObservable();

    this.dragDropService.dropSubject$.pipe(takeUntil(this.destroyed$)).subscribe((dropEvent: DropEvent) => {
      this.handleDrop(dropEvent.draggedItem, dropEvent.dropPosition);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  init(container: HTMLDivElement): Observable<void> {
    return this.floorplanWorkspace.init(this.selectedFloorplan, container).pipe(
      switchMap(() => this.floorplanWorkspaceItemService.addItems(this.selectedFloorplan, this.floorplanWorkspace)),
      switchMap(() =>
        this.floorplanWorkspaceWarningSignService.updateWarningSigns(
          this.selectedFloorplan,
          this.floorplanWorkspace,
          this.projectStateService.incompleteConfigIds$,
        ),
      ),
      tap(() => {
        this.initFloorplanEventHandler();
        this.floorplanWorkspace.selectedItemChanged$.pipe(takeUntil(this.destroyed$)).subscribe((selectedItem) => {
          this.selectedFloorplanItem = selectedItem.item ?? undefined;
        });
        this.floorplanWorkspace.selectedItemsChanged$.pipe(takeUntil(this.destroyed$)).subscribe((items) => {
          this.selectedFloorplanItems = items;
        });
      }),
    );
  }

  get selectedItem$(): Observable<{ item: FloorplanItem | null; selectIntention: ItemSelectIntention | null }> {
    return this.floorplanWorkspace.selectedItemChanged$;
  }

  get selectedItems$(): Observable<FloorplanItem[]> {
    return this.floorplanWorkspace.selectedItemsChanged$;
  }

  getSelectedItemsCount(): number {
    return this.floorplanWorkspace.getSelectedItems().length;
  }

  duplicateSelectedItem() {
    const currentSelected = this.floorplanWorkspace.getSelectedItem();
    if (!currentSelected) {
      return;
    }
    const duplicateSnapshot = currentSelected.createDuplicateSnapshot();
    if (!duplicateSnapshot) {
      return;
    }
    const position = this.calculateNextItemPosition(currentSelected.getDuplicateTargetCollection() ?? []);
    this.createAndSelectDuplicatedItem(duplicateSnapshot, position.x, position.y);
  }

  duplicateSelectedItems() {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    if (currentSelected.length === 0) {
      return;
    }

    const offset = { x: 20, y: 20 };

    const snapshots = currentSelected
      .map((item) => ({ snapshot: item.createDuplicateSnapshot(), x: item.x, y: item.y }))
      .filter(
        (entry): entry is { snapshot: (x: number, y: number) => FloorplanItem; x: number; y: number } =>
          entry.snapshot !== undefined,
      );

    if (snapshots.length === 0) {
      return;
    }

    const duplicates: FloorplanItem[] = [];
    this.suppressAddedItemSelection = true;
    try {
      for (const { snapshot, x, y } of snapshots) {
        duplicates.push(snapshot(x + offset.x, y + offset.y));
      }
    } finally {
      this.suppressAddedItemSelection = false;
    }

    this.floorplanWorkspace.deselect();
    for (const duplicate of duplicates) {
      this.floorplanWorkspace.selectItem(duplicate, ItemSelectIntention.REGULAR);
    }
  }

  deleteSelectedItem() {
    const currentSelected = this.floorplanWorkspace.getSelectedItem();
    if (!currentSelected || currentSelected.locked) {
      return;
    }
    currentSelected.delete();
  }

  deleteSelectedItems() {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    const itemsToDelete = currentSelected.filter((item) => !item.locked);
    for (const item of itemsToDelete) {
      item.delete();
    }
  }

  isProductGroupable(item: FloorplanItem): item is ProductTypesInGroup {
    return (
      item instanceof FloorplanAlarmDevice ||
      item instanceof FloorplanGasWarningCenter ||
      item instanceof FloorplanGasWarningCenterPlaceholder ||
      item instanceof FloorplanSimplePlaceholder ||
      item instanceof FloorplanPlasticSign ||
      item instanceof FloorplanSignalElement ||
      item instanceof FloorplanTransmitter ||
      item instanceof FloorplanTransmitterPlaceholder ||
      item instanceof FloorplanFlameDetector ||
      item instanceof FloorplanFlameDetectorPlaceholder ||
      item instanceof FloorplanPatientMonitor ||
      item instanceof FloorplanCentralStation ||
      item instanceof FloorplanTelemetry ||
      item instanceof FloorplanKvm ||
      item instanceof FloorplanDisplay ||
      item instanceof FloorplanUtility
    );
  }

  hasNonGroupableSelectedItems(): boolean {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    return currentSelected.some((item) => !this.isProductGroupable(item));
  }

  private getKindForItem(item: ProductTypesInGroup): FgdsProductType | MonitoringCategory | null {
    return getKindForItem(item);
  }

  selectedItemsHaveExistingGroup(): boolean {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    return currentSelected
      .filter((item) => this.isProductGroupable(item))
      .some((item) =>
        this.selectedFloorplan.project.groups.some((g) =>
          g.references.some(
            (r) => r.floorplanId === this.selectedFloorplan.id && r.floorplanProductItemId === (item as ProductTypesInGroup).id,
          ),
        ),
      );
  }

  /**
   * Returns the single group that all currently selected groupable items belong to.
   * Returns undefined if items are spread across multiple groups, if at least one
   * selected item is ungrouped, or if nothing groupable is selected.
   */
  getCommonGroupOfSelectedItems(): ProductItemGroup | undefined {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    const groupableItems = currentSelected.filter((item): item is ProductTypesInGroup => this.isProductGroupable(item));
    if (groupableItems.length === 0) return undefined;

    const groupsPerItem = groupableItems.map((item) =>
      this.selectedFloorplan.project.groups.find((g) =>
        g.references.some((r) => r.floorplanId === this.selectedFloorplan.id && r.floorplanProductItemId === item.id),
      ),
    );

    if (groupsPerItem.some((g) => !g)) return undefined;

    const firstGroup = groupsPerItem[0]!;
    const allShareSameGroup = groupsPerItem.every((g) => g!.id === firstGroup.id);
    return allShareSameGroup ? firstGroup : undefined;
  }

  groupSelectedItems(groupName: string, groupId: number) {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    const groupableItems = currentSelected.filter((item): item is ProductTypesInGroup => this.isProductGroupable(item));
    if (groupableItems.length === 0) return;

    const previousGroups = groupableItems
      .map((item) =>
        this.selectedFloorplan.project.groups.find((g) =>
          g.references.some((r) => r.floorplanId === this.selectedFloorplan.id && r.floorplanProductItemId === item.id),
        ),
      )
      .filter((g): g is ProductItemGroup => g !== undefined);

    this.floorplanWorkspace.deselect();

    const newGroup = new ProductItemGroup(groupName, groupId, [this.selectedFloorplan.id]);
    groupableItems.forEach((item) => {
      const kind = this.getKindForItem(item);
      if (!kind) return;
      // Remove from previous group
      previousGroups.forEach((g) => g.removeReference(this.selectedFloorplan.id, item.id));
      const ref = new FloorplanItemReference(this.selectedFloorplan.id, item.id, kind);
      this.selectedFloorplan.project.addProductItemReference(ref);
      newGroup.addReference(ref);
    });
    this.selectedFloorplan.project.addGroup(newGroup);

    const uniquePreviousGroups = [...new Set(previousGroups)];
    uniquePreviousGroups.forEach((group) => {
      this.selectedFloorplan.project.removeGroupIfEmpty(group);
    });

    this.selectedFloorplan.publishUpdate(FloorplanEventType.GROUP_ADDED, newGroup);
  }

  addSelectedItemsToGroup(targetGroup: ProductItemGroup) {
    const currentSelected = this.floorplanWorkspace.getSelectedItems();
    const groupableItems = currentSelected.filter((item): item is ProductTypesInGroup => this.isProductGroupable(item));
    if (groupableItems.length === 0) return;

    const allSelectedItems = [...currentSelected];
    const movedItems = new Set<FloorplanItem>();

    this.floorplanWorkspace.deselect();

    const isNewGroup = !this.selectedFloorplan.project.groups.some((g) => g.id === targetGroup.id);
    if (isNewGroup) {
      this.selectedFloorplan.project.addGroup(targetGroup);
      this.selectedFloorplan.publishUpdate(FloorplanEventType.GROUP_ADDED, targetGroup);
    }

    const emptySourceGroups = new Set<ProductItemGroup>();

    groupableItems.forEach((item) => {
      const sourceGroup = this.selectedFloorplan.project.groups.find((g) =>
        g.references.some((r) => r.floorplanId === this.selectedFloorplan.id && r.floorplanProductItemId === item.id),
      );
      if (sourceGroup?.id === targetGroup.id) return;
      movedItems.add(item);

      if (sourceGroup) {
        sourceGroup.removeReference(this.selectedFloorplan.id, item.id);
        emptySourceGroups.add(sourceGroup);
      }

      const kind = this.getKindForItem(item);
      if (kind) {
        const ref = new FloorplanItemReference(this.selectedFloorplan.id, item.id, kind);
        this.selectedFloorplan.project.addProductItemReference(ref);
        targetGroup.addReference(ref);
      }

      this.selectedFloorplan.publishUpdate(FloorplanEventType.GROUP_PRODUCT_ADDED, {
        product: item,
        targetGroup,
        sourceGroup,
      });
    });

    emptySourceGroups.forEach((group) => {
      this.selectedFloorplan.project.removeGroupIfEmpty(group);
    });

    allSelectedItems
      .filter((item) => !movedItems.has(item))
      .forEach((item) => this.floorplanWorkspace.selectItem(item, ItemSelectIntention.REGULAR));
  }

  copySelectedItem(): number {
    const isMultiMode = this.selectedFloorplan.floorplanState.selectionMode === FloorplanSelectionMode.MULTI;

    if (isMultiMode) {
      const selectedItems = this.floorplanWorkspace.getSelectedItems();
      if (selectedItems.length === 0) {
        this.copiedFloorplanState = undefined;
        return 0;
      }

      const snapshots = selectedItems
        .map((item) => ({ snapshot: item.createDuplicateSnapshot(), x: item.x, y: item.y }))
        .filter((entry): entry is CopiedFloorplanSnapshot => entry.snapshot !== undefined);

      if (snapshots.length === 0) {
        this.copiedFloorplanState = undefined;
        return 0;
      }

      this.copiedFloorplanState = { type: "multi", snapshots };
      return snapshots.length;
    } else {
      // Single mode
      const snapshot = this.selectedFloorplanItem?.createDuplicateSnapshot();
      this.copiedFloorplanState = snapshot ? { type: "single", snapshot } : undefined;
      return this.copiedFloorplanState ? 1 : 0;
    }
  }

  pasteCopiedItem() {
    if (!this.copiedFloorplanState) {
      return;
    }

    const pointerPosition = this.floorplanWorkspace.getPointerPosition();

    if (this.copiedFloorplanState.type === "multi") {
      // Multi-select paste: maintain relative positions
      const snapshots = this.copiedFloorplanState.snapshots;
      const offset = { x: 20, y: 20 };

      const duplicates: FloorplanItem[] = [];
      this.suppressAddedItemSelection = true;
      try {
        for (const { snapshot, x, y } of snapshots) {
          // Place relative to pointer position, maintaining relative distances from original
          const duplicateX = pointerPosition.x + (x - snapshots[0].x) + offset.x;
          const duplicateY = pointerPosition.y + (y - snapshots[0].y) + offset.y;
          duplicates.push(snapshot(duplicateX, duplicateY));
        }
      } finally {
        this.suppressAddedItemSelection = false;
      }

      this.floorplanWorkspace.deselect();
      for (const duplicate of duplicates) {
        this.floorplanWorkspace.selectItem(duplicate, ItemSelectIntention.REGULAR);
      }
    } else {
      // Single-select paste
      this.createAndSelectDuplicatedItem(this.copiedFloorplanState.snapshot, pointerPosition.x, pointerPosition.y);
    }
  }

  deselectItem() {
    this.floorplanWorkspace.deselect();
  }

  addAirPath(direction: AirPathDirection) {
    this.addItem((x, y) => this.selectedFloorplan.addAirPath(direction, x, y), this.selectedFloorplan.airPaths);
  }

  addAlarmDevice(config: AlarmDeviceConfiguration) {
    this.addItem((x, y, size) => this.selectedFloorplan.addAlarmDevice(config, x, y, size), this.selectedFloorplan.alarmDevices);
  }

  addAlarmDevicePlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addAlarmDevicePlaceholder(x, y, size),
      this.selectedFloorplan.alarmDevicePlaceholders,
    );
  }

  addGasWarningCenter(config: GasWarningCenterConfiguration) {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addGasWarningCenter(config, x, y, size),
      this.selectedFloorplan.gasWarningCenters,
    );
  }

  addGasWarningCenterPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addGasWarningCenterPlaceholder(x, y, size),
      this.selectedFloorplan.gasWarningCenterPlaceholders,
    );
  }

  addPlasticSign(config: PlasticSignConfiguration) {
    this.addItem((x, y, size) => this.selectedFloorplan.addPlasticSign(config, x, y, size), this.selectedFloorplan.plasticSigns);
  }

  addPlasticSignPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addPlasticSignPlaceholder(x, y, size),
      this.selectedFloorplan.plasticSignPlaceholders,
    );
  }

  addSignalElement(config: SignalElementConfiguration) {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addSignalElement(config, x, y, size),
      this.selectedFloorplan.signalElements,
    );
  }

  addSignalElementPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addSignalElementPlaceholder(x, y, size),
      this.selectedFloorplan.signalElementPlaceholders,
    );
  }

  addTransmitter(config: TransmitterConfiguration, remoteSensor?: FloorplanRemoteSensor) {
    const monitoringAreaRadius = this.getTransmitterMonitoringAreaRadius(config);

    this.addItem(
      (x, y, size) => this.selectedFloorplan.addTransmitter(config, x, y, size, monitoringAreaRadius, remoteSensor),
      this.selectedFloorplan.transmitters,
    );
  }

  addTransmitterPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addTransmitterPlaceholder(x, y, size, 375),
      this.selectedFloorplan.transmitterPlaceholders,
    );
  }

  addFlameDetector(config: FlameDetectorConfiguration) {
    const coverageAreaLength = this.getFlameDetectorCoverageAreaLength(config);

    this.addItem(
      (x, y, size) => this.selectedFloorplan.addFlameDetector(config, x, y, size, coverageAreaLength),
      this.selectedFloorplan.flameDetectors,
    );
  }

  addFlameDetectorPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addFlameDetectorPlaceholder(x, y, size, 375),
      this.selectedFloorplan.flameDetectorPlaceholders,
    );
  }

  addImage(projectImage: ProjectImageMetadata) {
    this.addItem((x, y) => this.selectedFloorplan.addImage(projectImage, x, y), this.selectedFloorplan.images);
  }

  addMeasurementLine() {
    this.addItem(
      (x, y) => this.selectedFloorplan.addMeasurementLine(x, y, $localize`:@@floorplan.measurementLine.defaultText:1 Meter`),
      this.selectedFloorplan.measurementLines,
    );
  }

  addReferenceMeasure() {
    this.addItem((x, y) => this.selectedFloorplan.addReferenceMeasure(x, y, 1), []);
  }

  addText() {
    this.addItem((x, y, size) => this.selectedFloorplan.addFloorplanText(x, y, size), this.selectedFloorplan.floorplanTexts);
  }

  addFreeText(content: string) {
    this.addItem((x, y) => this.selectedFloorplan.addFloorplanFreeText(x, y, content), this.selectedFloorplan.floorplanFreeTexts);
  }

  addPipeline() {
    this.addItem((x, y) => this.selectedFloorplan.addPipeline(x, y), this.selectedFloorplan.pipelines);
  }

  addCableRoute() {
    this.addItem((x, y) => this.selectedFloorplan.addCableRoute(x, y), this.selectedFloorplan.cableRoutes);
  }

  addProtectiveTube() {
    this.addItem((x, y) => this.selectedFloorplan.addProtectiveTube(x, y), this.selectedFloorplan.protectiveTubes);
  }

  addWire() {
    this.addItem((x, y) => this.selectedFloorplan.addWire(x, y), this.selectedFloorplan.wires);
  }

  addPatientMonitor(config: PatientMonitorConfiguration) {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addPatientMonitor(config, x, y, size),
      this.selectedFloorplan.patientMonitors,
    );
  }

  addPatientMonitorPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addPatientMonitorPlaceholder(x, y, size),
      this.selectedFloorplan.patientMonitorPlaceholders,
    );
  }

  addCentralStation(config: CentralStationConfiguration) {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addCentralStation(config, x, y, size),
      this.selectedFloorplan.centralStations,
    );
  }

  addCentralStationPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addCentralStationPlaceholder(x, y, size),
      this.selectedFloorplan.centralStationPlaceholders,
    );
  }

  addTelemetry(config: TelemetryConfiguration) {
    this.addItem((x, y, size) => this.selectedFloorplan.addTelemetry(config, x, y, size), this.selectedFloorplan.telemetries);
  }

  addTelemetryPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addTelemetryPlaceholder(x, y, size),
      this.selectedFloorplan.telemetryPlaceholders,
    );
  }

  addKvm(config: KvmConfiguration) {
    this.addItem((x, y, size) => this.selectedFloorplan.addKvm(config, x, y, size), this.selectedFloorplan.kvms);
  }

  addKvmPlaceholder() {
    this.addItem((x, y, size) => this.selectedFloorplan.addKvmPlaceholder(x, y, size), this.selectedFloorplan.kvmPlaceholders);
  }

  addDisplay(config: DisplayConfiguration) {
    this.addItem((x, y, size) => this.selectedFloorplan.addDisplay(config, x, y, size), this.selectedFloorplan.displays);
  }

  addDisplayPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addDisplayPlaceholder(x, y, size),
      this.selectedFloorplan.displayPlaceholders,
    );
  }

  addUtility(config: UtilityConfiguration) {
    this.addItem((x, y, size) => this.selectedFloorplan.addUtility(config, x, y, size), this.selectedFloorplan.utilities);
  }

  addUtilityPlaceholder() {
    this.addItem(
      (x, y, size) => this.selectedFloorplan.addUtilityPlaceholder(x, y, size),
      this.selectedFloorplan.utilityPlaceholders,
    );
  }

  zoomOut() {
    this.selectedFloorplan.floorplanState.zoomOut(this.calculateStageCenter());
  }

  zoomIn() {
    this.selectedFloorplan.floorplanState.zoomIn(this.calculateStageCenter());
  }

  resetZoom() {
    const { width: stageWidth, height: stageHeight } = this.floorplanWorkspace.getStageDimensions();
    const { width: borderWidth, height: borderHeight } = this.floorplanWorkspace.getBorderDimensions();

    const scaleWidth = stageWidth / borderWidth;
    const scaleHeight = stageHeight / borderHeight;
    const scale = Math.min(scaleWidth, scaleHeight) * 0.9;

    this.selectedFloorplan.floorplanState.updateScaleAndPosition(
      scale,
      (stageWidth - borderWidth * scale) / 2,
      (stageHeight - borderHeight * scale) / 2,
    );
  }

  getBorderDimensions() {
    return this.floorplanWorkspace.getBorderDimensions();
  }

  private initFloorplanEventHandler() {
    this.projectFloorplanService.floorplanEvents$
      .pipe(
        filter((event) => event.floorplan === this.selectedFloorplan),
        takeUntil(this.destroyed$),
      )
      .subscribe((event) => {
        const selectAddedItem = !this.suppressAddedItemSelection;
        switch (event.type) {
          case FloorplanEventType.EX_ZONE_ADDED:
            this.floorplanWorkspace.addExZone(event.subject as ExZone, selectAddedItem);
            break;
          case FloorplanEventType.DUST_EX_ZONE_ADDED:
            this.floorplanWorkspace.addDustExZone(event.subject as DustExZone, selectAddedItem);
            break;
          case FloorplanEventType.WILDCARD_ZONE_ADDED:
            this.floorplanWorkspace.addWildcardZone(event.subject as WildcardZone, selectAddedItem);
            break;
          case FloorplanEventType.DANGER_AREA_ADDED:
            this.floorplanWorkspace.addDangerArea(event.subject as DangerArea, selectAddedItem);
            break;
          case FloorplanEventType.IMAGE_ADDED:
            this.floorplanWorkspace.addImage(event.subject as FloorplanImage, selectAddedItem).subscribe({
              error: (err) => console.error("Failed to add image to floorplan:", err),
            });
            break;
          case FloorplanEventType.MEASUREMENT_LINE_ADDED:
            this.floorplanWorkspace.addMeasurementLine(event.subject as MeasurementLine, selectAddedItem);
            break;
          case FloorplanEventType.REFERENCE_MEASURE_ADDED:
            this.floorplanWorkspace.addReferenceMeasure(event.subject as ReferenceMeasure, selectAddedItem);
            this.updateReferenceSize();
            this.updateReferenceLength();
            this._isReferenceMeasureSet$.next(!!this.selectedFloorplan?.referenceMeasure);
            break;
          case FloorplanEventType.TEXT_ADDED:
            this.floorplanWorkspace.addText(event.subject as FloorplanText, selectAddedItem);
            break;
          case FloorplanEventType.FREE_TEXT_ADDED:
            this.floorplanWorkspace.addFreeText(event.subject as FloorplanFreeText, selectAddedItem);
            break;
          case FloorplanEventType.AIR_PATH_ADDED:
            this.floorplanWorkspace.addAirPath(event.subject as AirPath, selectAddedItem);
            break;
          case FloorplanEventType.PIPELINE_ADDED:
            this.floorplanWorkspace.addPipeline(event.subject as Pipeline, selectAddedItem);
            break;
          case FloorplanEventType.CABLE_ROUTE_ADDED:
            this.floorplanWorkspace.addCableRoute(event.subject as CableRoute, selectAddedItem);
            break;
          case FloorplanEventType.PROTECTIVE_TUBE_ADDED:
            this.floorplanWorkspace.addProtectiveTube(event.subject as ProtectiveTube, selectAddedItem);
            break;
          case FloorplanEventType.WIRE_ADDED:
            this.floorplanWorkspace.addWire(event.subject as Wire, selectAddedItem);
            break;
          case FloorplanEventType.TRANSMITTER_ADDED:
          case FloorplanEventType.TRANSMITTER_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addTransmitter(
              event.subject as FloorplanTransmitterPlaceholder | FloorplanTransmitter,
              selectAddedItem,
            );
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.FLAME_DETECTOR_ADDED:
          case FloorplanEventType.FLAME_DETECTOR_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addFlameDetector(
              event.subject as FloorplanFlameDetectorPlaceholder | FloorplanFlameDetector,
              selectAddedItem,
            );
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.ALARM_DEVICE_ADDED:
          case FloorplanEventType.ALARM_DEVICE_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addAlarmDevice(
              event.subject as FloorplanSimplePlaceholder | FloorplanAlarmDevice,
              selectAddedItem,
            );
            break;
          case FloorplanEventType.GAS_WARNING_CENTER_ADDED:
          case FloorplanEventType.GAS_WARNING_CENTER_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addGasWarningCenter(
              event.subject as FloorplanGasWarningCenterPlaceholder | FloorplanGasWarningCenter,
              selectAddedItem,
            );
            break;
          case FloorplanEventType.SIGNAL_ELEMENT_ADDED:
          case FloorplanEventType.SIGNAL_ELEMENT_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addSignalElement(
              event.subject as FloorplanSimplePlaceholder | FloorplanSignalElement,
              selectAddedItem,
            );
            break;
          case FloorplanEventType.PLASTIC_SIGN_ADDED:
          case FloorplanEventType.PLASTIC_SIGN_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addPlasticSign(
              event.subject as FloorplanSimplePlaceholder | FloorplanPlasticSign,
              selectAddedItem,
            );
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.PATIENT_MONITOR_ADDED:
            this.floorplanWorkspace.addPatientMonitor(event.subject as FloorplanPatientMonitor, selectAddedItem);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.PATIENT_MONITOR_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addPatientMonitor(
              event.subject as FloorplanSimplePlaceholder | FloorplanPatientMonitor,
              selectAddedItem,
            );
            break;
          case FloorplanEventType.CENTRAL_STATION_ADDED:
            this.floorplanWorkspace.addCentralStation(event.subject as FloorplanCentralStation, selectAddedItem);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.CENTRAL_STATION_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addCentralStation(
              event.subject as FloorplanSimplePlaceholder | FloorplanCentralStation,
              selectAddedItem,
            );
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.TELEMETRY_ADDED:
            this.floorplanWorkspace.addTelemetry(event.subject as FloorplanTelemetry, selectAddedItem);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.TELEMETRY_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addTelemetry(
              event.subject as FloorplanSimplePlaceholder | FloorplanTelemetry,
              selectAddedItem,
            );
            break;
          case FloorplanEventType.KVM_ADDED:
            this.floorplanWorkspace.addKvm(event.subject as FloorplanKvm, selectAddedItem);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.KVM_RECEIVERS_UPDATED:
            this.floorplanWorkspace.syncKvmReceivers(event.subject as FloorplanKvm);
            break;
          case FloorplanEventType.KVM_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addKvm(event.subject as FloorplanSimplePlaceholder | FloorplanKvm, selectAddedItem);
            break;
          case FloorplanEventType.DISPLAY_ADDED:
            this.floorplanWorkspace.addDisplay(event.subject as FloorplanDisplay, true);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.DISPLAY_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addDisplay(event.subject as FloorplanSimplePlaceholder | FloorplanDisplay, true);
            break;
          case FloorplanEventType.UTILITY_ADDED:
            this.floorplanWorkspace.addUtility(event.subject as FloorplanUtility, true);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.UTILITY_PLACEHOLDER_ADDED:
            this.floorplanWorkspace.addUtility(event.subject as FloorplanSimplePlaceholder | FloorplanUtility, true);
            break;
          case FloorplanEventType.ISOLATION_ROOM_ADDED:
            this.floorplanWorkspace.addIsolationRoom(event.subject as IsolationRoom, selectAddedItem);
            break;
          case FloorplanEventType.IT_ROOM_ADDED:
            this.floorplanWorkspace.addItRoom(event.subject as ItRoom, selectAddedItem);
            break;
          case FloorplanEventType.OPERATING_THEATRE_ADDED:
            this.floorplanWorkspace.addOperatingTheatre(event.subject as OperatingTheatre, selectAddedItem);
            break;
          case FloorplanEventType.PROCEDURE_ROOM_ADDED:
            this.floorplanWorkspace.addProcedureRoom(event.subject as ProcedureRoom, selectAddedItem);
            break;
          case FloorplanEventType.STANDARD_ROOM_ADDED:
            this.floorplanWorkspace.addStandardRoom(event.subject as StandardRoom, selectAddedItem);
            break;
          case FloorplanEventType.GROUP_ADDED:
            this.floorplanWorkspace.createGroup(event.subject as ProductItemGroup);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.GROUP_DELETED:
            this.floorplanWorkspace.deleteGroup(event.subject as ProductItemGroup);
            break;
          case FloorplanEventType.GROUP_DESELECTED:
            this.floorplanWorkspace.deselectGroup(event.subject as ProductItemGroup);
            break;
          case FloorplanEventType.GROUP_PRODUCT_ADDED:
            this.floorplanWorkspace.changeGroup(event.subject);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.GROUP_PRODUCT_REMOVED:
            this.floorplanWorkspace.removeFromGroup(event.subject);
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.GROUP_SELECTED:
            this.floorplanWorkspace.selectGroup(event.subject as ProductItemGroup);
            break;
          case FloorplanEventType.EX_ZONE_DELETED:
          case FloorplanEventType.DUST_EX_ZONE_DELETED:
          case FloorplanEventType.WILDCARD_ZONE_DELETED:
          case FloorplanEventType.DANGER_AREA_DELETED:
          case FloorplanEventType.IMAGE_DELETED:
          case FloorplanEventType.MEASUREMENT_LINE_DELETED:
          case FloorplanEventType.TEXT_DELETED:
          case FloorplanEventType.FREE_TEXT_DELETED:
          case FloorplanEventType.AIR_PATH_DELETED:
          case FloorplanEventType.PIPELINE_DELETED:
          case FloorplanEventType.CABLE_ROUTE_DELETED:
          case FloorplanEventType.PROTECTIVE_TUBE_DELETED:
          case FloorplanEventType.WIRE_DELETED:
          case FloorplanEventType.TRANSMITTER_DELETED:
          case FloorplanEventType.TRANSMITTER_PLACEHOLDER_DELETED:
          case FloorplanEventType.FLAME_DETECTOR_DELETED:
          case FloorplanEventType.FLAME_DETECTOR_PLACEHOLDER_DELETED:
          case FloorplanEventType.ALARM_DEVICE_DELETED:
          case FloorplanEventType.ALARM_DEVICE_PLACEHOLDER_DELETED:
          case FloorplanEventType.GAS_WARNING_CENTER_DELETED:
          case FloorplanEventType.GAS_WARNING_CENTER_PLACEHOLDER_DELETED:
          case FloorplanEventType.SIGNAL_ELEMENT_DELETED:
          case FloorplanEventType.SIGNAL_ELEMENT_PLACEHOLDER_DELETED:
          case FloorplanEventType.PLASTIC_SIGN_DELETED:
          case FloorplanEventType.PLASTIC_SIGN_PLACEHOLDER_DELETED:
          case FloorplanEventType.ISOLATION_ROOM_DELETED:
          case FloorplanEventType.IT_ROOM_DELETED:
          case FloorplanEventType.OPERATING_THEATRE_DELETED:
          case FloorplanEventType.PROCEDURE_ROOM_DELETED:
          case FloorplanEventType.STANDARD_ROOM_DELETED:
          case FloorplanEventType.PATIENT_MONITOR_DELETED:
          case FloorplanEventType.PATIENT_MONITOR_PLACEHOLDER_DELETED:
          case FloorplanEventType.CENTRAL_STATION_DELETED:
          case FloorplanEventType.CENTRAL_STATION_PLACEHOLDER_DELETED:
          case FloorplanEventType.TELEMETRY_DELETED:
          case FloorplanEventType.TELEMETRY_PLACEHOLDER_DELETED:
          case FloorplanEventType.KVM_DELETED:
          case FloorplanEventType.KVM_PLACEHOLDER_DELETED:
          case FloorplanEventType.DISPLAY_DELETED:
          case FloorplanEventType.DISPLAY_PLACEHOLDER_DELETED:
          case FloorplanEventType.UTILITY_DELETED:
          case FloorplanEventType.UTILITY_PLACEHOLDER_DELETED:
            this.floorplanWorkspace.deleteItem(event.subject as FloorplanItem);
            break;
          case FloorplanEventType.REFERENCE_MEASURE_DELETED:
            this.updateReferenceSize();
            this.updateReferenceLength();
            this.floorplanWorkspace.deleteItem(event.subject as FloorplanItem);
            this._isReferenceMeasureSet$.next(!!this.selectedFloorplan?.referenceMeasure);
            this.selectedFloorplan.floorplanState.coverageAreasVisible = false;
            break;

          case FloorplanEventType.ITEM_LOCK_CHANGED:
            this.floorplanWorkspace.setLockState(event.subject as FloorplanItem);
            break;
          case FloorplanEventType.ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.setItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.ITEM_POSITION_ID_CHANGED:
            this.floorplanWorkspace.refreshItemText(event.subject as FloorplanItem);
            break;
          case FloorplanEventType.ITEM_DISPLAY_NAME_CHANGED:
            this.floorplanWorkspace.refreshDisplayName(event.subject as FloorplanItem);
            break;
          case FloorplanEventType.FLOORPLAN_STATE_VISIBILITY_CHANGED:
            this.floorplanWorkspace.setVisibilityState(event.subject as FloorplanState);
            break;
          case FloorplanEventType.FLOORPLAN_STATE_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.setGlobalItemTextVisibilityState(event.subject as FloorplanState);
            break;
          case FloorplanEventType.MEASUREMENT_LINE_UPDATED:
            this.floorplanWorkspace.refreshMeasurementLine(event.subject as MeasurementLine);
            break;
          case FloorplanEventType.REFERENCE_MEASURE_UPDATED:
            this.updateTransmitterMonitoringAreaRadius();
            this.updateFlameDetectorCoverageAreaLength();
            this.updateReferenceSize();
            this.updateReferenceLength();
            this.floorplanWorkspace.refreshReferenceMeasure(event.subject as ReferenceMeasure);
            break;
          case FloorplanEventType.REFERENCE_MEASURE_RESET:
            this.updateTransmitterMonitoringAreaRadius();
            this.updateFlameDetectorCoverageAreaLength();
            this.updateReferenceSize();
            this.updateReferenceLength();
            this.floorplanWorkspace.resetReferenceMeasure(event.subject);
            break;
          case FloorplanEventType.MONITORING_AREA_RADIUS_UPDATED:
            this.floorplanWorkspace.autoScaleItems();
            break;
          case FloorplanEventType.COVERAGE_AREA_LENGTH_UPDATED:
            this.floorplanWorkspace.autoScaleItems();
            break;
          case FloorplanEventType.FLOORPLAN_STATE_POSITION_UPDATED: {
            const floorplanState = event.subject as FloorplanState;
            this.floorplanWorkspace.setStagePosition({ x: floorplanState.x, y: floorplanState.y });
            break;
          }
          case FloorplanEventType.FLOORPLAN_STATE_SCALE_UPDATED:
            this.floorplanWorkspace.setScale((event.subject as FloorplanState).scale);
            break;
          case FloorplanEventType.FLOORPLAN_DISPLAY_MODE_UPDATED:
            this.floorplanWorkspace.setDisplayMode((event.subject as FloorplanState).displayMode);
            break;
          case FloorplanEventType.FLOORPLAN_OPACITY_UPDATED:
            this.floorplanWorkspace.refreshFloorplanOpacity((event.subject as FloorplanState).opacity);
            break;
          case FloorplanEventType.FLOORPLAN_SELECTION_MODE_UPDATED:
            this.floorplanWorkspace.deselect();
            break;
          case FloorplanEventType.ITEM_SIZE_CHANGED:
            if (event.subject instanceof FloorplanProductItem || event.subject instanceof FloorplanText) {
              this.currentIconSize = event.subject.size;
            }
            break;
          case FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT:
            if (event.subject instanceof DangerArea) {
              this.floorplanWorkspace.refreshZone(event.subject as DangerArea);
            }
            if (event.subject instanceof DustExZone) {
              this.floorplanWorkspace.refreshZone(event.subject as DustExZone);
            }
            if (event.subject instanceof ExZone) {
              this.floorplanWorkspace.refreshZone(event.subject as ExZone);
            }
            if (event.subject instanceof WildcardZone) {
              this.floorplanWorkspace.refreshZone(event.subject as WildcardZone);
            }
            if (event.subject instanceof AirPath) {
              this.floorplanWorkspace.refreshAirPath(event.subject as AirPath);
            }
            if (event.subject instanceof CableRoute) {
              this.floorplanWorkspace.refreshCableRoute(event.subject as CableRoute);
            }
            if (event.subject instanceof Pipeline) {
              this.floorplanWorkspace.refreshPipeline(event.subject as Pipeline);
            }
            if (event.subject instanceof ProtectiveTube) {
              this.floorplanWorkspace.refreshProtectiveTube(event.subject as ProtectiveTube);
            }
            if (event.subject instanceof Wire) {
              this.floorplanWorkspace.refreshWire(event.subject as Wire);
            }
            if (event.subject instanceof MeasurementLine) {
              this.floorplanWorkspace.refreshMeasurementLine(event.subject as MeasurementLine);
            }
            if (event.subject instanceof IsolationRoom) {
              this.floorplanWorkspace.refreshZone(event.subject as IsolationRoom);
            }
            if (event.subject instanceof ItRoom) {
              this.floorplanWorkspace.refreshZone(event.subject as ItRoom);
            }
            if (event.subject instanceof OperatingTheatre) {
              this.floorplanWorkspace.refreshZone(event.subject as OperatingTheatre);
            }
            if (event.subject instanceof ProcedureRoom) {
              this.floorplanWorkspace.refreshZone(event.subject as ProcedureRoom);
            }
            if (event.subject instanceof StandardRoom) {
              this.floorplanWorkspace.refreshZone(event.subject as StandardRoom);
            }
            break;
          case FloorplanEventType.ITEM_UPDATED:
            this.floorplanWorkspaceWarningSignService
              .updateWarningSigns(event.floorplan, this.floorplanWorkspace, this.projectStateService.incompleteConfigIds$)
              .pipe(take(1))
              .subscribe();
            break;
          case FloorplanEventType.REMOTE_SENSOR_ATTACHED:
            if (event.subject instanceof FloorplanTransmitter || event.subject instanceof FloorplanTransmitterPlaceholder) {
              this.floorplanWorkspace.attachRemoteSensor(event.subject, selectAddedItem);
            }
            break;
          case FloorplanEventType.REMOTE_SENSOR_DETACHED:
            if (event.subject instanceof FloorplanTransmitter || event.subject instanceof FloorplanTransmitterPlaceholder) {
              this.floorplanWorkspace.detachRemoteSensor(event.subject);
            }
            break;
          case FloorplanEventType.AIR_PATHS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateAirPathsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.ALARM_DEVICES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateAlarmDevicesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.CABLE_ROUTES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateCableRoutesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.DANGER_AREAS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateDangerAreasItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.DUST_EX_ZONES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateDustExZonesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.EX_ZONES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateExZonesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.GAS_WARNING_CENTERS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateGasWarningCentersItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.IMAGES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateImagesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.MEASUREMENT_LINES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateMeasurementLinesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.REFERENCE_MEASURE_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateReferenceMeasureItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.PIPELINES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updatePipelinesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.PLASTIC_SIGNS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updatePlasticSignsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.PROTECTIVE_TUBES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateProtectiveTubesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.SIGNAL_ELEMENTS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateSignalElementsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.TEXTS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateTextsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.FREE_TEXTS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateFreeTextsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.TRANSMITTERS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateTransmittersItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.FLAME_DETECTORS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateFlameDetectorsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.WILDCARD_ZONES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateWildcardZonesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.WIRES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateWiresItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.PATIENT_MONITORS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updatePatientMonitorsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.CENTRAL_STATIONS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateCentralStationsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.TELEMETRIES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateTelemetriesItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.KVMS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateKvmsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.DISPLAYS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateDisplaysItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.ISOLATION_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateIsolationRoomsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.IT_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateItRoomsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.OPERATING_THEATRES_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateOperatingTheatresItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.PROCEDURE_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateProcedureRoomsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.STANDARD_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED:
            this.floorplanWorkspace.updateStandardRoomsItemTextVisibility(event.subject);
            break;
          case FloorplanEventType.FREE_TEXT_UPDATED:
            this.floorplanWorkspace.refreshFreeText(event.subject);
        }
      });
  }

  private updateReferenceSize() {
    this.updateZoneReferenceSizes([
      ...this.selectedFloorplan.dangerAreas,
      ...this.selectedFloorplan.exZones,
      ...this.selectedFloorplan.dustExZones,
      ...this.selectedFloorplan.wildcardZones,
      ...this.selectedFloorplan.isolationRooms,
      ...this.selectedFloorplan.itRooms,
      ...this.selectedFloorplan.operatingTheatres,
      ...this.selectedFloorplan.procedureRooms,
      ...this.selectedFloorplan.standardRooms,
    ]);
  }

  private updateZoneReferenceSizes(zones: ReferenceSizeZone[]) {
    zones.forEach((zone) => {
      zone.updateReferenceSize(zone.width, zone.height);
      zone.referenceSizeChanged.next({ referenceWidth: zone.referenceWidth, referenceHeight: zone.referenceHeight });
    });
  }

  private updateReferenceLength() {
    this.selectedFloorplan.airPaths.forEach((airPath) => {
      airPath.updateReferenceLength(airPath.length);
      airPath.referenceLengthChanged.next({ referenceLength: airPath.referenceLength });
    });
    this.selectedFloorplan.cableRoutes.forEach((cableRoute) => {
      cableRoute.updateReferenceLength(cableRoute.width);
      cableRoute.referenceLengthChanged.next({ referenceLength: cableRoute.referenceLength });
    });
    this.selectedFloorplan.pipelines.forEach((pipeline) => {
      pipeline.updateReferenceLength(pipeline.width);
      pipeline.referenceLengthChanged.next({ referenceLength: pipeline.referenceLength });
    });
    this.selectedFloorplan.protectiveTubes.forEach((protectiveTube) => {
      protectiveTube.updateReferenceLength(protectiveTube.width);
      protectiveTube.referenceLengthChanged.next({ referenceLength: protectiveTube.referenceLength });
    });
    this.selectedFloorplan.wires.forEach((wire) => {
      wire.updateReferenceLength(wire.width);
      wire.referenceLengthChanged.next({ referenceLength: wire.referenceLength });
    });
    this.selectedFloorplan.measurementLines.forEach((line) => {
      line.updateReferenceLength(line.width);
      this.floorplanWorkspace.refreshMeasurementLine(line);
      line.referenceLengthChanged.next({ referenceLength: line.referenceLength });
    });
    this.selectedFloorplan.transmitters.forEach((transmitter) => {
      transmitter.updateMonitoringAreaRadius(transmitter.referenceMonitoringAreaRadius);
    });
    this.selectedFloorplan.transmitterPlaceholders.forEach((transmitterPlaceholder) => {
      transmitterPlaceholder.updateMonitoringAreaRadius(transmitterPlaceholder.referenceMonitoringAreaRadius);
    });
    this.selectedFloorplan.flameDetectors.forEach((flameDetector) => {
      flameDetector.updateCoverageAreaLength(flameDetector.referenceCoverageAreaLength);
    });
    this.selectedFloorplan.flameDetectorPlaceholders.forEach((flameDetectorPlaceholder) => {
      flameDetectorPlaceholder.updateCoverageAreaLength(flameDetectorPlaceholder.referenceCoverageAreaLength);
    });
  }

  private updateTransmitterMonitoringAreaRadius() {
    this.selectedFloorplan.transmitters.forEach((transmitter) => {
      transmitter.updateMonitoringAreaRadiusByReferenceMeasure();
    });

    this.selectedFloorplan.transmitterPlaceholders.forEach((transmitterPlaceholder) => {
      transmitterPlaceholder.updateMonitoringAreaRadiusByReferenceMeasure();
    });
  }

  private updateFlameDetectorCoverageAreaLength() {
    this.selectedFloorplan.flameDetectors.forEach((flameDetector) => {
      flameDetector.updateCoverageAreaLengthByReferenceMeasure();
    });

    this.selectedFloorplan.flameDetectorPlaceholders.forEach((flameDetectorPlaceholder) => {
      flameDetectorPlaceholder.updateCoverageAreaLengthByReferenceMeasure();
    });
  }

  addItem(addFunction: (x: number, y: number, iconSize: number) => void, existingItems: FloorplanItem[]) {
    const position = this.calculateNextItemPosition(existingItems);
    addFunction(position.x, position.y, this.currentIconSize);
  }

  private calculateNextItemPosition(existingItems: FloorplanItem[]): Point {
    const position = this.floorplanWorkspace.getCenterPoint();
    const randomMax = 100;
    do {
      position.x += Math.floor(Math.random() * randomMax) - randomMax / 2;
      position.y += Math.floor(Math.random() * randomMax) - randomMax / 2;
    } while (existingItems.some((floorplanItem) => floorplanItem.x == position.x && floorplanItem.y == position.y));
    return position;
  }

  private calculateStageCenter(): Point {
    const stageDimensions = this.floorplanWorkspace.getStageDimensions();
    return new Point(stageDimensions.width / 2, stageDimensions.height / 2);
  }

  private getTransmitterMonitoringAreaRadius(config: TransmitterConfiguration): number {
    if (this.selectedFloorplan.referenceMeasure) {
      return config.defaultMonitoringAreaRadius * this.selectedFloorplan.getReferenceScale();
    }
    return this.currentIconSize * 50;
  }

  private getFlameDetectorCoverageAreaLength(config: FlameDetectorConfiguration): number {
    if (this.selectedFloorplan.referenceMeasure) {
      return config.defaultCoverageAreaLength * this.selectedFloorplan.getReferenceScale();
    }
    return this.currentIconSize * 50;
  }

  private createAndSelectDuplicatedItem(factory: (x: number, y: number) => FloorplanItem, x: number, y: number) {
    let duplicate: FloorplanItem | undefined;
    // suppress auto-selection from the add event; select explicitly below with the correct intention
    this.suppressAddedItemSelection = true;
    try {
      duplicate = factory(x, y);
    } finally {
      this.suppressAddedItemSelection = false;
    }

    if (duplicate) {
      this.floorplanWorkspace.selectItem(duplicate, ItemSelectIntention.REGULAR);
    }
  }

  private handleDrop(floorplanItem: DraggedItem, dropPosition: DropPosition) {
    if (floorplanItem.type === DraggedItemType.GAS_WARNING_CENTER) {
      this.selectedFloorplan.addGasWarningCenter(
        floorplanItem.config as GasWarningCenterConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );
    } else if (floorplanItem.type === DraggedItemType.TRANSMITTER) {
      const monitoringAreaRadius = this.getTransmitterMonitoringAreaRadius(floorplanItem.config as TransmitterConfiguration);

      this.selectedFloorplan.addTransmitter(
        floorplanItem.config as TransmitterConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
        monitoringAreaRadius,
      );
    } else if (floorplanItem.type === DraggedItemType.FLAME_DETECTOR) {
      const coverageAreaLength = this.getFlameDetectorCoverageAreaLength(floorplanItem.config as FlameDetectorConfiguration);

      this.selectedFloorplan.addFlameDetector(
        floorplanItem.config as FlameDetectorConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
        coverageAreaLength,
      );
    } else if (floorplanItem.type === DraggedItemType.SIGNAL_ELEMENT) {
      this.selectedFloorplan.addSignalElement(
        floorplanItem.config as SignalElementConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );
    } else if (floorplanItem.type === DraggedItemType.PLASTIC_SIGN) {
      this.selectedFloorplan.addPlasticSign(
        floorplanItem.config as PlasticSignConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );
    } else if (floorplanItem.type === DraggedItemType.ALARM_DEVICE) {
      this.selectedFloorplan.addAlarmDevice(
        floorplanItem.config as AlarmDeviceConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );
    } else if (floorplanItem.type === DraggedItemType.DANGER_AREA) {
      this.selectedFloorplan.addDangerArea(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.PIPELINE) {
      this.selectedFloorplan.addPipeline(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.PROTECTIVE_TUBE) {
      this.selectedFloorplan.addProtectiveTube(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.CABLE_ROUTE) {
      this.selectedFloorplan.addCableRoute(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.WIRE) {
      this.selectedFloorplan.addWire(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.NOTES) {
      this.selectedFloorplan.addFloorplanText(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.FREE_TEXT) {
      this.selectedFloorplan.addFloorplanFreeText(dropPosition.x, dropPosition.y, floorplanItem.freeTextPlaceholder);
    } else if (floorplanItem.type === DraggedItemType.MEASUREMENT_LINE) {
      this.selectedFloorplan.addMeasurementLine(
        dropPosition.x,
        dropPosition.y,
        $localize`:@@floorplan.measurementLine.defaultText:1 Meter`,
      );
    } else if (floorplanItem.type === DraggedItemType.REFERENCE_MEASURE) {
      this.selectedFloorplan.addReferenceMeasure(dropPosition.x, dropPosition.y, 1);
    } else if (floorplanItem.type === DraggedItemType.EXHAUST_AIR) {
      this.selectedFloorplan.addAirPath(floorplanItem.config as AirPathDirection, dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.SUPPLY_AIR) {
      this.selectedFloorplan.addAirPath(floorplanItem.config as AirPathDirection, dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.IMAGE) {
      this.selectedFloorplan.addImage(floorplanItem.config as ProjectImageMetadata, dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.GAS_WARNING_CENTER_PLACEHOLDER) {
      this.selectedFloorplan.addGasWarningCenterPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.TRANSMITTER_PLACEHOLDER) {
      this.selectedFloorplan.addTransmitterPlaceholder(
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
        this.currentIconSize * 50,
      );
    } else if (floorplanItem.type === DraggedItemType.SIGNAL_ELEMENT_PLACEHOLDER) {
      this.selectedFloorplan.addSignalElementPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.PLASTIC_SIGN_PLACEHOLDER) {
      this.selectedFloorplan.addPlasticSignPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.ALARM_DEVICE_PLACEHOLDER) {
      this.selectedFloorplan.addAlarmDevicePlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.EX_ZONE) {
      this.selectedFloorplan.addExZone(floorplanItem.config as ExZoneType, dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.DUST_EX_ZONE) {
      this.selectedFloorplan.addDustExZone(floorplanItem.config as DustExZoneType, dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.WILDCARD_ZONE) {
      this.selectedFloorplan.addWildcardZone(floorplanItem.config as WildcardZoneType, dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.ISOLATION_ROOM) {
      this.selectedFloorplan.addIsolationRoom(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.IT_ROOM) {
      this.selectedFloorplan.addItRoom(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.OPERATING_THEATRE) {
      this.selectedFloorplan.addOperatingTheatre(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.PROCEDURE_ROOM) {
      this.selectedFloorplan.addProcedureRoom(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.STANDARD_ROOM) {
      this.selectedFloorplan.addStandardRoom(dropPosition.x, dropPosition.y);
    } else if (floorplanItem.type === DraggedItemType.PATIENT_MONITOR) {
      this.selectedFloorplan.addPatientMonitor(
        floorplanItem.config as PatientMonitorConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );

      this.dragDropService.clear();
    } else if (floorplanItem.type === DraggedItemType.PATIENT_MONITOR_PLACEHOLDER) {
      this.selectedFloorplan.addPatientMonitorPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.CENTRAL_STATION) {
      this.selectedFloorplan.addCentralStation(
        floorplanItem.config as CentralStationConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );

      this.dragDropService.clear();
    } else if (floorplanItem.type === DraggedItemType.CENTRAL_STATION_PLACEHOLDER) {
      this.selectedFloorplan.addCentralStationPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.TELEMETRY) {
      this.selectedFloorplan.addTelemetry(
        floorplanItem.config as TelemetryConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );
      this.dragDropService.clear();
    } else if (floorplanItem.type === DraggedItemType.TELEMETRY_PLACEHOLDER) {
      this.selectedFloorplan.addTelemetryPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.UTILITY) {
      this.selectedFloorplan.addUtility(
        floorplanItem.config as UtilityConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );
      this.dragDropService.clear();
    } else if (floorplanItem.type === DraggedItemType.UTILITY_PLACEHOLDER) {
      this.selectedFloorplan.addUtilityPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.KVM) {
      this.selectedFloorplan.addKvm(
        floorplanItem.config as KvmConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );

      this.dragDropService.clear();
    } else if (floorplanItem.type === DraggedItemType.KVM_PLACEHOLDER) {
      this.selectedFloorplan.addKvmPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    } else if (floorplanItem.type === DraggedItemType.DISPLAY) {
      this.selectedFloorplan.addDisplay(
        floorplanItem.config as DisplayConfiguration,
        dropPosition.x,
        dropPosition.y,
        this.currentIconSize,
      );

      this.dragDropService.clear();
    } else if (floorplanItem.type === DraggedItemType.DISPLAY_PLACEHOLDER) {
      this.selectedFloorplan.addDisplayPlaceholder(dropPosition.x, dropPosition.y, this.currentIconSize);
    }
  }
}
